import React from "react";
import { useAutoPoll } from "./useAutoPoll";
import { backendUrl } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { Activity, DollarSign, FileCheck2, AlertTriangle, Timer, RefreshCw, Settings } from "lucide-react";

// ─── Shared UI primitives ─────────────────────────────────────────
export const STATUS_COLORS = {
  green:  { dot: "bg-emerald-500", txt: "text-emerald-700", bg: "bg-emerald-50", label: "Working" },
  yellow: { dot: "bg-amber-500",   txt: "text-amber-700",   bg: "bg-amber-50",   label: "Warning" },
  red:    { dot: "bg-red-500",     txt: "text-red-700",     bg: "bg-red-50",     label: "Error"  },
  unlimited: { dot: "bg-neutral-400", txt: "text-neutral-600", bg: "bg-neutral-100", label: "No limit" },
  ok:     { dot: "bg-emerald-500", txt: "text-emerald-700", bg: "bg-emerald-50", label: "OK" },
  warn:   { dot: "bg-amber-500",   txt: "text-amber-700",   bg: "bg-amber-50",   label: "Warning" },
  over:   { dot: "bg-red-500",     txt: "text-red-700",     bg: "bg-red-50",     label: "Over" },
};

export function StatusPill({ status }) {
  const cfg = STATUS_COLORS[status] || STATUS_COLORS.yellow;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs ${cfg.bg} ${cfg.txt}`}>
      <span className={`h-2 w-2 rounded-full ${cfg.dot}`} />{cfg.label}
    </span>
  );
}

export function RefreshChip({ lastUpdated, loading }) {
  const ago = lastUpdated ? Math.round((Date.now() - lastUpdated.getTime()) / 1000) : null;
  return (
    <span className="inline-flex items-center gap-1 text-xs text-neutral-500" data-testid="refresh-chip">
      <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
      {ago === null ? "loading…" : `updated ${ago}s ago`}
    </span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, tone = "neutral", testId }) {
  const tones = {
    neutral: "border-neutral-200",
    positive: "border-emerald-200 bg-emerald-50/30",
    warning: "border-amber-200 bg-amber-50/30",
    danger:  "border-red-200 bg-red-50/30",
  };
  return (
    <div className={`border rounded-lg p-4 ${tones[tone]}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-wide">
        {Icon && <Icon size={14} />} {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums text-neutral-900">{value}</div>
      {sub && <div className="text-xs text-neutral-500 mt-1">{sub}</div>}
    </div>
  );
}

// ─── Production Status Banner ─────────────────────────────────────
const PROD_STATUS_STYLE = {
  green:  { emoji: "🟢", ring: "ring-emerald-500", bg: "bg-emerald-50", text: "text-emerald-900", tint: "text-emerald-700" },
  yellow: { emoji: "🟡", ring: "ring-amber-500",   bg: "bg-amber-50",   text: "text-amber-900",   tint: "text-amber-700" },
  red:    { emoji: "🔴", ring: "ring-red-500",     bg: "bg-red-50",     text: "text-red-900",     tint: "text-red-700" },
};

function ProductionStatusBanner({ status }) {
  if (!status) return null;
  const s = PROD_STATUS_STYLE[status.level] || PROD_STATUS_STYLE.yellow;
  return (
    <div className={`rounded-xl ${s.bg} ring-1 ${s.ring} px-5 py-4 mb-6 flex items-start justify-between gap-4`}
         data-testid={`production-status-${status.level}`}>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">{s.emoji}</span>
        <div>
          <div className={`text-xs uppercase tracking-widest ${s.tint}`}>Production Status</div>
          <div className={`text-xl font-semibold ${s.text}`} data-testid="production-status-label">
            {status.label}
          </div>
          <div className={`text-sm mt-1 ${s.tint}`}>
            {(status.reasons || []).join(" · ")}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Budget Monitor ───────────────────────────────────────────────
const LEVEL_STYLE = {
  ok:        { pill: "bg-emerald-50 text-emerald-800", bar: "bg-emerald-500", label: "Green" },
  warn:      { pill: "bg-amber-50 text-amber-800",     bar: "bg-amber-500",   label: "Yellow" },
  over:      { pill: "bg-red-50 text-red-800",          bar: "bg-red-500",     label: "Red" },
  critical:  { pill: "bg-red-100 text-red-900 font-semibold", bar: "bg-red-600", label: "Critical" },
  unlimited: { pill: "bg-neutral-100 text-neutral-500", bar: "bg-neutral-300", label: "No limit" },
};

function BudgetBar({ label, bucket }) {
  const level = bucket?.level || "unlimited";
  const s = LEVEL_STYLE[level] || LEVEL_STYLE.unlimited;
  const pct = Math.min(100, bucket?.pct ?? 0);
  return (
    <div data-testid={`budget-bucket-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between text-xs mb-1.5">
        <span className="uppercase tracking-wide text-neutral-500">{label}</span>
        <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase ${s.pill}`}>
          {s.label}
        </span>
      </div>
      <div className="flex items-baseline gap-1 mb-1">
        <span className="text-lg font-semibold tabular-nums">${(bucket?.spent_usd ?? 0).toFixed(2)}</span>
        <span className="text-xs text-neutral-500">/ ${(bucket?.limit_usd ?? 0).toFixed(2)}</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className={`h-full ${s.bar} transition-all`} style={{ width: `${pct}%` }} />
      </div>
      <div className="text-xs text-neutral-500 mt-1 tabular-nums">
        {bucket?.pct?.toFixed?.(1) ?? "0.0"}% · ${(bucket?.remaining_usd ?? 0).toFixed(2)} left
      </div>
    </div>
  );
}

function BudgetMonitor({ budget }) {
  if (!budget) return null;
  const remaining = budget.estimated_articles_remaining_month;
  const remainingDays = budget.estimated_days_remaining_month;
  const fc = budget.forecast || {};
  return (
    <Card data-testid="budget-monitor">
      <CardHeader className="flex-row items-center justify-between gap-2">
        <CardTitle className="text-base flex items-center gap-2">
          Budget Monitor
          {budget.hard_limit ? (
            <Badge variant="destructive" className="text-xs">Hard limit ON</Badge>
          ) : (
            <Badge variant="secondary" className="text-xs">Monitoring only</Badge>
          )}
        </CardTitle>
        <Link to="/seo-studio/config" className="text-xs text-neutral-500 hover:text-neutral-900 underline">
          Configure limits
        </Link>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
          <BudgetBar label="Today" bucket={budget.today} />
          <BudgetBar label="Week"  bucket={budget.week} />
          <BudgetBar label="Month" bucket={budget.month} />
        </div>

        {/* Forecast row */}
        <div className="border-t border-neutral-100 pt-3 mb-3">
          <div className="text-xs uppercase tracking-wide text-neutral-500 mb-2">
            Forecast — at current rate ${fc.daily_rate_usd?.toFixed?.(4) ?? "0.0000"}/day
          </div>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <div data-testid="forecast-7d">
              <div className="text-xs text-neutral-500">Next 7 days</div>
              <div className="text-lg font-semibold tabular-nums">${(fc["7d_usd"] ?? 0).toFixed(2)}</div>
            </div>
            <div data-testid="forecast-30d">
              <div className="text-xs text-neutral-500">Next 30 days</div>
              <div className="text-lg font-semibold tabular-nums">${(fc["30d_usd"] ?? 0).toFixed(2)}</div>
            </div>
            <div data-testid="forecast-90d">
              <div className="text-xs text-neutral-500">Next 90 days</div>
              <div className="text-lg font-semibold tabular-nums">${(fc["90d_usd"] ?? 0).toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="border-t border-neutral-100 pt-3 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wide">Avg article cost</div>
            <div className="text-lg font-semibold tabular-nums" data-testid="budget-avg-cost">
              ${(budget.avg_article_cost_usd ?? 0).toFixed(4)}
            </div>
            <div className="text-xs text-neutral-500">{budget.articles_analysed ?? 0} runs</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wide">Articles remaining (month)</div>
            <div className="text-lg font-semibold tabular-nums" data-testid="budget-articles-remaining">
              {remaining == null ? "—" : remaining}
            </div>
            <div className="text-xs text-neutral-500">at current cost/article</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wide">Days remaining (month)</div>
            <div className="text-lg font-semibold tabular-nums" data-testid="budget-days-remaining">
              {remainingDays == null ? "—" : remainingDays}
            </div>
            <div className="text-xs text-neutral-500">at current rate</div>
          </div>
          <div>
            <div className="text-xs text-neutral-500 uppercase tracking-wide">Enforcement</div>
            <div className="text-sm font-semibold">
              {budget.hard_limit ? "Hard limit — blocks runs" : "Warn only"}
            </div>
            <div className="text-xs text-neutral-500">Warn at {budget.warn_at_pct}%</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Unit Economics ───────────────────────────────────────────────
function UnitEconomics({ ue }) {
  if (!ue) return null;
  const avg = ue.avg_per_article_usd || {};
  const total = avg.total ?? 0;
  const cats = ["llm", "dataforseo", "textru", "images", "other"];
  return (
    <Card data-testid="unit-economics">
      <CardHeader>
        <CardTitle className="text-base">Unit Economics — avg per article (30d)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums mb-2" data-testid="unit-economics-total">
          ${total.toFixed(4)}
        </div>
        <div className="text-xs text-neutral-500 mb-4">
          Based on {ue.successful_articles ?? 0} successful articles in the last 30 days.
        </div>
        <div className="space-y-1.5">
          {cats.map((c) => {
            const v = avg[c] ?? 0;
            const pct = total > 0 ? (v / total * 100) : 0;
            return (
              <div key={c} className="flex items-center gap-3 text-xs" data-testid={`ue-cat-${c}`}>
                <span className="w-24 text-neutral-600 capitalize">{c}</span>
                <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                  <div className="h-full bg-neutral-800 transition-all"
                       style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
                <span className="w-24 text-right tabular-nums text-neutral-700">
                  ${v.toFixed(4)} <span className="text-neutral-400">({pct.toFixed(0)}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Top KPI Row ─────────────────────────────────────────────────
function TopKpiRow({ data, economics }) {
  const t = data.today || {};
  const b = data.budget || {};
  const totals = data.totals || {};
  const started = t.runs_started ?? 0;
  const failed = t.runs_failed ?? 0;
  const successRate = started > 0 ? (100 * (started - failed) / started) : 100;
  const avgCost = (b.avg_article_cost_usd ?? 0);
  const avgTime = data.stage_avg_ms
    ? Math.round(Object.values(data.stage_avg_ms).reduce((a, b) => a + b, 0))
    : 0;
  const budgetRem = b.month?.remaining_usd ?? 0;
  const articlesRem = b.estimated_articles_remaining_month;

  const items = [
    { label: "Articles today",     value: started,                            testId: "kpi-articles-today" },
    { label: "Published today",    value: t.runs_published ?? 0,               tone: "positive", testId: "kpi-published-top" },
    { label: "Success rate",       value: `${successRate.toFixed(0)}%`,        tone: successRate < 80 ? "warning" : "positive", testId: "kpi-success-rate" },
    { label: "Avg article cost",   value: `$${avgCost.toFixed(4)}`,            testId: "kpi-avg-cost" },
    { label: "Avg time (30d)",     value: `${(avgTime / 1000).toFixed(1)}s`,   testId: "kpi-avg-time" },
    { label: "Budget remaining",   value: `$${budgetRem.toFixed(2)}`,          tone: b.month?.level === "over" || b.month?.level === "critical" ? "danger" : "positive", testId: "kpi-budget-remaining" },
    { label: "Articles remaining", value: articlesRem == null ? "—" : articlesRem,  testId: "kpi-articles-remaining" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-6" data-testid="top-kpi-row">
      {items.map((it) => (
        <KpiCard key={it.label} label={it.label} value={it.value} tone={it.tone || "neutral"} testId={it.testId} />
      ))}
    </div>
  );
}

// ─── Economics Detail Card (fetched separately) ──────────────────
function useEconomics() {
  const [d, setD] = React.useState(null);
  React.useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const r = await fetch(backendUrl("/api/seo/ops/economics"),
                                { headers: { Authorization: `JWT ${localStorage.getItem("seo:jwt") || ""}` } });
        if (r.ok && !cancelled) setD(await r.json());
      } catch (_) { /* ignore transient errors */ }
    }
    load();
    const t = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return d;
}

function EconomicsDetail() {
  const d = useEconomics();
  if (!d) return null;
  const trend = d.trend || {};
  const cats  = d.total_by_category_usd || {};
  const catsAvg = d.avg_by_category_usd || {};
  const mostExpensive = d.most_expensive_article;
  const cheapest      = d.cheapest_article;
  const top           = d.top_budget_consumer_today;
  const rows = ["llm", "dataforseo", "textru", "images", "other"];
  return (
    <Card data-testid="economics-detail" className="mb-8">
      <CardHeader><CardTitle className="text-base">Cost Trend & Extremes</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-1 lg:grid-cols-2 gap-6 text-sm">
        <div>
          <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Cost per article — trend</div>
          <table className="w-full text-xs tabular-nums">
            <thead className="text-neutral-500">
              <tr><th className="text-left py-1">Window</th><th className="text-right">Runs</th><th className="text-right">Avg</th><th className="text-right">Median</th><th className="text-right">Min</th><th className="text-right">Max</th></tr>
            </thead>
            <tbody>
              {["7d", "30d", "90d"].map((w) => (
                <tr key={w} className="border-t border-neutral-100">
                  <td className="py-1.5">{w}</td>
                  <td className="text-right">{trend[w]?.count ?? 0}</td>
                  <td className="text-right">${(trend[w]?.avg ?? 0).toFixed(4)}</td>
                  <td className="text-right">${(trend[w]?.median ?? 0).toFixed(4)}</td>
                  <td className="text-right">${(trend[w]?.min ?? 0).toFixed(4)}</td>
                  <td className="text-right">${(trend[w]?.max ?? 0).toFixed(4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-neutral-500">Cost per published (30d):</span>{" "}
              <b className="tabular-nums">${(d.cost_per_published_article_usd ?? 0).toFixed(4)}</b>
            </div>
            <div>
              <span className="text-neutral-500">Cost per draft (30d):</span>{" "}
              <b className="tabular-nums">${(d.cost_per_draft_usd ?? 0).toFixed(4)}</b>
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs text-neutral-500 uppercase tracking-wide mb-2">Category breakdown (30d)</div>
          <div className="space-y-1.5">
            {rows.map((c) => (
              <div key={c} className="flex items-center gap-3 text-xs" data-testid={`ecodetail-${c}`}>
                <span className="w-24 capitalize text-neutral-700">{c}</span>
                <div className="flex-1 text-right tabular-nums">
                  total <b>${(cats[c] ?? 0).toFixed(4)}</b>{" "}
                  <span className="text-neutral-400">·</span>{" "}
                  avg/article <b>${(catsAvg[c] ?? 0).toFixed(4)}</b>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 border-t border-neutral-100 pt-3 space-y-2 text-xs">
            {top && (
              <div>
                <span className="text-neutral-500">Top consumer today:</span>{" "}
                <b>{top.service || "—"}</b> · ${top.cost_usd.toFixed(4)} ({top.calls} calls)
              </div>
            )}
            {mostExpensive && (
              <div>
                <span className="text-neutral-500">Most expensive:</span>{" "}
                <a href={`/seo-studio/runs/${mostExpensive.id}`} className="underline hover:no-underline font-mono">
                  {mostExpensive.id.slice(0, 8)}
                </a>{" · "}
                <b>${mostExpensive.total_cost_usd?.toFixed?.(4)}</b>
                {mostExpensive.model && <span className="text-neutral-500"> · {mostExpensive.model}</span>}
              </div>
            )}
            {cheapest && (
              <div>
                <span className="text-neutral-500">Cheapest:</span>{" "}
                <a href={`/seo-studio/runs/${cheapest.id}`} className="underline hover:no-underline font-mono">
                  {cheapest.id.slice(0, 8)}
                </a>{" · "}
                <b>${cheapest.total_cost_usd?.toFixed?.(4)}</b>
                {cheapest.model && <span className="text-neutral-500"> · {cheapest.model}</span>}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Queue counters ───────────────────────────────────────────────
function QueueCounters({ counts }) {
  if (!counts) return null;
  const items = [
    { k: "running",   label: "Running",   tone: "bg-blue-50 text-blue-800 border-blue-200" },
    { k: "paused",    label: "Paused",    tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { k: "pending",   label: "Pending",   tone: "bg-neutral-50 text-neutral-700 border-neutral-200" },
    { k: "waiting",   label: "Waiting",   tone: "bg-neutral-50 text-neutral-700 border-neutral-200" },
    { k: "retrying",  label: "Retrying",  tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { k: "warning",   label: "Warning",   tone: "bg-amber-50 text-amber-800 border-amber-200" },
    { k: "failed",    label: "Failed",    tone: "bg-red-50 text-red-800 border-red-200" },
    { k: "cancelled", label: "Cancelled", tone: "bg-red-50 text-red-700 border-red-200" },
    { k: "success",   label: "Completed", tone: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  ];
  return (
    <Card className="mb-8" data-testid="queue-counters">
      <CardHeader><CardTitle className="text-base">Queue status</CardTitle></CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 md:grid-cols-9 gap-2">
          {items.map((it) => (
            <div key={it.k} className={`border rounded-lg px-3 py-2.5 ${it.tone}`}
                    data-testid={`queue-${it.k}`}>
              <div className="text-[10px] uppercase tracking-wider opacity-80">{it.label}</div>
              <div className="text-xl font-semibold tabular-nums mt-0.5">{counts[it.k] ?? 0}</div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Operational Dashboard ────────────────────────────────────────
export default function OpsDashboard() {
  const { data, loading, error, lastUpdated } = useAutoPoll(
    backendUrl("/api/seo/ops/dashboard"), { intervalMs: 12000 });

  if (error) return <ErrorState message={error} />;
  if (!data)  return <LoadingState />;

  const t = data.today || {};
  const m = data.month || {};
  const tot = data.totals || {};
  const health = data.health || {};

  const failedTone = t.runs_failed > 0 ? "danger" : "positive";
  const runsInFlightTone = t.runs_in_flight > 0 ? "warning" : "neutral";

  return (
    <div className="p-6" data-testid="ops-dashboard">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">SEO Studio — Operations</h1>
          <p className="text-sm text-neutral-500 mt-1">Live status of the content production pipeline.</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/seo-studio/config" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
                data-testid="link-to-config">
            <Settings size={14} /> Configuration
          </Link>
          <RefreshChip lastUpdated={lastUpdated} loading={loading} />
        </div>
      </div>

      {/* Production Status — first thing after title */}
      <ProductionStatusBanner status={data.production_status} />

      {/* KPI top row — Phase 2 Block 4 */}
      <TopKpiRow data={data} />

      {/* Today */}
      <h2 className="text-sm font-semibold text-neutral-600 mb-3 uppercase tracking-wide">Today</h2>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
        <KpiCard icon={FileCheck2} label="Published"      value={t.runs_published ?? 0}
                    tone="positive" testId="kpi-published-today" />
        <KpiCard icon={Activity}   label="Started"        value={t.runs_started ?? 0}
                    testId="kpi-started-today" />
        <KpiCard icon={Timer}      label="In-flight"      value={t.runs_in_flight ?? 0}
                    tone={runsInFlightTone} testId="kpi-in-flight" />
        <KpiCard icon={AlertTriangle} label="Failed"      value={t.runs_failed ?? 0}
                    tone={failedTone} testId="kpi-failed-today" />
        <KpiCard icon={DollarSign} label="Cost today"     value={`$${(t.cost_usd ?? 0).toFixed(3)}`}
                    sub={`${t.calls ?? 0} API calls`} testId="kpi-cost-today" />
        <KpiCard icon={DollarSign} label="Cost month"     value={`$${(m.cost_usd ?? 0).toFixed(2)}`}
                    sub={`${m.calls ?? 0} API calls`} testId="kpi-cost-month" />
      </div>

      {/* Budget Monitor + Unit Economics side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        <BudgetMonitor budget={data.budget} />
        <UnitEconomics ue={data.unit_economics} />
      </div>

      {/* Queue counters — Phase 2 Block 3 */}
      <QueueCounters counts={data.queue_counts} />

      {/* Economics Detail — Phase 2 Block 4 */}
      <EconomicsDetail />

      {/* Totals */}
      <h2 className="text-sm font-semibold text-neutral-600 mb-3 uppercase tracking-wide">Content totals</h2>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-8">
        <KpiCard label="Briefs" value={tot.briefs ?? 0}   testId="kpi-briefs" />
        <KpiCard label="Runs"   value={tot.runs ?? 0}     testId="kpi-runs" />
        <KpiCard label="Published" value={tot.published ?? 0} tone="positive" testId="kpi-published-total" />
        <KpiCard label="Drafts" value={tot.drafts ?? 0}   testId="kpi-drafts" />
        <KpiCard label="Avg words" value={tot.avg_words ?? 0}
                    sub={`quality ${tot.avg_quality_score ?? 0}`} testId="kpi-avg-words" />
        <KpiCard label="Avg uniqueness"
                    value={`${(tot.avg_uniqueness_pct ?? 0).toFixed(1)}%`}
                    sub={(tot.avg_uniqueness_pct ?? 0) >= 75 ? "above threshold" : "below threshold"}
                    tone={(tot.avg_uniqueness_pct ?? 0) >= 75 ? "positive" : "warning"}
                    testId="kpi-avg-uniqueness" />
      </div>

      {/* Health strip */}
      <Card className="mb-6" data-testid="dashboard-health">
        <CardHeader className="flex-row items-center justify-between gap-2">
          <CardTitle className="text-base">Service health</CardTitle>
          <StatusPill status={health.overall} />
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(health.services || []).map((s) => (
              <div key={s.name}
                     className="flex items-center justify-between border rounded-md px-3 py-2 text-sm"
                     data-testid={`health-item-${s.name.toLowerCase().replace(/\W+/g, "-")}`}>
                <span className="text-neutral-800">{s.name}</span>
                <div className="flex items-center gap-2">
                  {s.response_ms != null && <span className="text-xs text-neutral-500">{s.response_ms}ms</span>}
                  <StatusPill status={s.status} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Bottleneck */}
      {data.bottleneck && (
        <Card data-testid="dashboard-bottleneck">
          <CardHeader><CardTitle className="text-base">Stage performance (last 30d)</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-4">
              <div data-testid="ceo-fastest">
                <div className="text-xs text-neutral-500 uppercase">Fastest stage</div>
                <div className="text-base font-semibold">{data.fastest_stage?.service || "—"}</div>
                <div className="text-xs text-neutral-500 tabular-nums">
                  {data.fastest_stage?.avg_ms ? `${Math.round(data.fastest_stage.avg_ms)}ms avg` : "—"}
                </div>
              </div>
              <div data-testid="ceo-slowest">
                <div className="text-xs text-neutral-500 uppercase">Slowest stage</div>
                <div className="text-base font-semibold">{data.slowest_stage?.service || "—"}</div>
                <div className="text-xs text-neutral-500 tabular-nums">
                  {data.slowest_stage?.avg_ms ? `${Math.round(data.slowest_stage.avg_ms)}ms avg` : "—"}
                </div>
              </div>
              <div data-testid="ceo-top-service">
                <div className="text-xs text-neutral-500 uppercase">Top service today</div>
                <div className="text-base font-semibold">{data.top_service_today?.service || "—"}</div>
                <div className="text-xs text-neutral-500 tabular-nums">
                  ${(data.top_service_today?.cost_usd ?? 0).toFixed(4)} · {data.top_service_today?.calls ?? 0} calls
                </div>
              </div>
              <div data-testid="ceo-failure-rate">
                <div className="text-xs text-neutral-500 uppercase">Failure / Recovery (30d)</div>
                <div className="text-base font-semibold tabular-nums">
                  {data.failure_rate_pct_30d ?? 0}% / {data.recovery_rate_pct_30d ?? 0}%
                </div>
              </div>
            </div>
            {data.stage_avg_ms && Object.keys(data.stage_avg_ms).length > 0 && (
              <div className="mt-3 space-y-1.5">
                {Object.entries(data.stage_avg_ms).sort((a,b) => b[1]-a[1]).map(([svc, ms]) => (
                  <div key={svc} className="flex items-center gap-3 text-xs">
                    <span className="w-32 text-neutral-600 truncate">{svc}</span>
                    <div className="flex-1 h-2 bg-neutral-100 rounded-full overflow-hidden">
                      <div className="h-full bg-neutral-800"
                             style={{ width: `${Math.min(100, ms / Math.max(...Object.values(data.stage_avg_ms)) * 100)}%` }} />
                    </div>
                    <span className="w-16 text-right tabular-nums text-neutral-500">{Math.round(ms)}ms</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Alerts */}
      {(data.alerts?.length > 0) && (
        <Card className="mt-6" data-testid="dashboard-alerts">
          <CardHeader><CardTitle className="text-base flex items-center gap-2">
            <span>Active Alerts</span>
            <Badge variant="destructive" className="text-xs">{data.alerts.length}</Badge>
          </CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.alerts.map((a, i) => (
                <div key={i} className="flex items-center gap-3 text-sm border-l-2 border-red-500 pl-3 py-1"
                       data-testid={`alert-${a.kind}`}>
                  <Badge variant="outline" className="uppercase text-[10px]">{a.severity}</Badge>
                  <span className="text-neutral-500 uppercase text-xs">{a.kind}</span>
                  <span className="text-neutral-900">{a.message}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

export function LoadingState() {
  return (
    <div className="p-8 text-sm text-neutral-500" data-testid="ops-loading">
      Loading operational data…
    </div>
  );
}

export function ErrorState({ message }) {
  return (
    <div className="p-8" data-testid="ops-error">
      <Badge variant="destructive">Error</Badge>
      <p className="mt-3 text-sm text-neutral-700">{message}</p>
    </div>
  );
}
